import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BaseGetByIdRequest } from '@app-types/base/base';
import { MailAccountClient } from '@app-services/api/clients/mail-account.client';
import { MailAccountContract } from '@app-types/api/mail-account';
import { MatchError } from 'src/app/services/errors/error-matcher';
import { EmailGroupClient } from '@app-services/api/clients/email-group.client';
import { GroupClient } from '@app-services/api/clients/group-settings.client';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SomethingWentWrongComponent } from '../../common/error/something-went-wrong/something-went-wrong.component';
import { LoadingButtonComponent } from '../../common/loading-button/loading-button.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { defaultFilter } from '@app-shared/constants/constants';
import { DrawerService } from '@app-services/drawer.service';
import { TagContract } from '@app-types/api/tag';
import { IGroup } from '@app-types/api/email';

@Component({
  selector: 'app-email-add-groups-dialog',
  templateUrl: './email-add-groups-dialog.component.html',
  styleUrls: ['./email-add-groups-dialog.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    SomethingWentWrongComponent,
    LoadingButtonComponent,
    MatCheckboxModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
  ],
})
export class EmailAddGroupsDialogComponent implements OnInit {
  @Input() mailAccountId: number;
  @Input() emailIds: number[];
  @Output() submitEventEmitter = new EventEmitter<{
    groups: IGroup[];
  }>();
  @Output() public closeEventEmitter = new EventEmitter<any>();
  public thisGroups: any[] = [];
  public existedGroups: any[] = [];
  public isLoading = true;
  public existedGroupsResponse: any;
  private mailAccount: MailAccountContract;
  public thisGroupsIds: number[] = [];
  public resultGroupsIds: number[] = [];
  public isError = false;
  public isSavingGroups = false;

  constructor(
    public emailGroupClient: EmailGroupClient,
    private groupClient: GroupClient,
    public matchError: MatchError,
    private mailAccountClient: MailAccountClient,
    private drawerService: DrawerService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  async loadData(): Promise<void> {
    this.isError = false;
    this.isLoading = true;
    try {
      const response = await this.mailAccountClient.getById(
        new BaseGetByIdRequest(this.mailAccountId)
      );
      this.mailAccount = response.mailAccount;
      const request = {
        organizationId: this.mailAccount.organizationId,
        offset: 0,
        fetch: 500,
        filter: {
          ...defaultFilter,
        },
        search: '',
      };
      this.existedGroupsResponse =
        await this.groupClient.getAllByOrganization(request);
      this.existedGroups = this.existedGroupsResponse?.data ?? [];
      if (this.emailIds.length === 1) {
        this.thisGroups = await this.emailGroupClient.getGroupsForEmails({
          list: this.emailIds,
        });
        this.thisGroupsIds = this.thisGroups
          .map(e => e.groupId)
          .filter((e, i, arr) => arr.indexOf(e) === i);
      }
    } catch (e) {
      this.isError = true;
      this.matchError.logError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public isChecked(groupId: number): boolean {
    return this.thisGroupsIds.includes(groupId);
  }

  async addGroupToEmails(isCheck: boolean, groupId: number): Promise<void> {
    if (isCheck) {
      this.thisGroupsIds.push(groupId);
    } else {
      this.thisGroupsIds = this.thisGroupsIds.filter(e => e !== groupId);
    }
  }

  async ok(): Promise<any> {
    try {
      if (this.existedGroups.length === 0) {
        this.drawerService.closeDrawer();
        return;
      }
      this.drawerService.disabledDrawer(true);
      this.isSavingGroups = true;
      if (this.thisGroupsIds.length) {
        await this.emailGroupClient.addGroupsToEmails({
          sourceIds: this.emailIds,
          toIds: this.thisGroupsIds,
        });
      } else {
        const allGroupIds = this.existedGroups.map(e => e?.groupId);
        await this.emailGroupClient.removeGroupsFromEmails({
          sourceIds: this.emailIds,
          toIds: allGroupIds,
        });
      }
      this.resultGroupsIds = this.thisGroupsIds;
      const res = this.existedGroups.filter(e =>
        this.resultGroupsIds.includes(e.groupId)
      );
      this.submitEventEmitter.emit({ groups: res });
      this.drawerService.disabledDrawer(false);
      this.drawerService.closeDrawer();
    } catch (e) {
      this.drawerService.disabledDrawer(false);
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isSavingGroups = false;
    }
  }
}
